<script setup lang="ts">
const { state, currentStepData, close, next, prev, isFirst, isLast, totalSteps } = useSpotlight();
const { t } = useI18n();

const highlightRef = ref<HTMLElement | null>(null);
const clickBlockerRef = ref<HTMLElement | null>(null);
const tooltipRef = ref<HTMLElement | null>(null);
const targetElement = ref<HTMLElement | null>(null);

const updatePositions = () => {
  if (!currentStepData.value?.target) {
    return;
  }

  const element = document.querySelector(currentStepData.value.target) as HTMLElement | null;
  targetElement.value = element;

  if (element) {
    const rect = element.getBoundingClientRect();
    const padding = 8;

    const top = rect.top - padding;
    const left = rect.left - padding;
    const width = rect.width + padding * 2;
    const height = rect.height + padding * 2;

    if (highlightRef.value) {
      highlightRef.value.style.top = `${top}px`;
      highlightRef.value.style.left = `${left}px`;
      highlightRef.value.style.width = `${width}px`;
      highlightRef.value.style.height = `${height}px`;
    }

    // Update click blocker clip-path to have a hole for the cutout
    if (clickBlockerRef.value) {
      const right = left + width;
      const bottom = top + height;
      clickBlockerRef.value.style.clipPath = `polygon(
        0% 0%, 0% 100%,
        ${left}px 100%, ${left}px ${top}px,
        ${right}px ${top}px, ${right}px ${bottom}px,
        ${left}px ${bottom}px, ${left}px 100%,
        100% 100%, 100% 0%
      )`;
    }

    if (tooltipRef.value) {
      updateTooltipPosition(rect);
    }
  }
};

const updateTooltipPosition = (rect: DOMRect) => {
  if (!tooltipRef.value) return;

  const tooltip = tooltipRef.value;
  const placement = currentStepData.value?.placement || 'bottom';
  const gap = 16;
  const margin = 12;

  // Reset styles
  tooltip.style.top = '';
  tooltip.style.bottom = '';
  tooltip.style.left = '';
  tooltip.style.right = '';
  tooltip.style.transform = '';

  const tooltipRect = tooltip.getBoundingClientRect();

  if (placement === 'top' || placement === 'bottom') {
    // Horizontal centering with viewport constraints
    let left = rect.left + rect.width / 2 - tooltipRect.width / 2;
    left = Math.max(margin, Math.min(left, window.innerWidth - tooltipRect.width - margin));
    tooltip.style.left = `${left}px`;

    if (placement === 'top') {
      tooltip.style.bottom = `${window.innerHeight - rect.top + gap}px`;
    } else {
      tooltip.style.top = `${rect.bottom + gap}px`;
    }
  } else {
    // Vertical centering with viewport constraints
    let top = rect.top + rect.height / 2 - tooltipRect.height / 2;
    top = Math.max(margin, Math.min(top, window.innerHeight - tooltipRect.height - margin));
    tooltip.style.top = `${top}px`;

    if (placement === 'left') {
      tooltip.style.right = `${window.innerWidth - rect.left + gap}px`;
    } else {
      tooltip.style.left = `${rect.right + gap}px`;
    }
  }
};

// Use ResizeObserver to track element size changes
let resizeObserver: ResizeObserver | null = null;

const setupObserver = () => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }

  resizeObserver = new ResizeObserver(() => {
    updatePositions();
  });

  // Observe body for any layout changes
  resizeObserver.observe(document.body);

  if (targetElement.value) {
    resizeObserver.observe(targetElement.value);
  }
};

watch(
  () => [state.value.active, state.value.currentStep],
  () => {
    if (state.value.active) {
      nextTick(() => {
        updatePositions();
        setupObserver();
      });
    } else {
      resizeObserver?.disconnect();
    }
  },
  { immediate: true },
);

// Handle wheel/touch events on tooltip to scroll underlying content
const handleWheel = (e: WheelEvent) => {
  const scrollContainer = document.querySelector('.overflow-y-auto') || document.documentElement;
  scrollContainer.scrollTop += e.deltaY;
  updatePositions();
};

let touchStartY = 0;
const handleTouchStart = (e: TouchEvent) => {
  touchStartY = e.touches[0]?.clientY ?? 0;
};

const handleTouchMove = (e: TouchEvent) => {
  const touchY = e.touches[0]?.clientY ?? 0;
  const deltaY = touchStartY - touchY;
  touchStartY = touchY;

  const scrollContainer = document.querySelector('.overflow-y-auto') || document.documentElement;
  scrollContainer.scrollTop += deltaY;
  updatePositions();
};

onMounted(() => {
  window.addEventListener('resize', updatePositions);
  window.addEventListener('scroll', updatePositions, true);
});

onUnmounted(() => {
  window.removeEventListener('resize', updatePositions);
  window.removeEventListener('scroll', updatePositions, true);
  resizeObserver?.disconnect();
});
</script>

<template>
  <Teleport to="body">
    <Transition name="spotlight">
      <div v-if="state.active" class="spotlight-container">
        <!-- Invisible overlay to block clicks outside cutout (has clip-path hole) -->
        <div ref="clickBlockerRef" class="spotlight-click-blocker" @wheel="handleWheel" @touchstart="handleTouchStart" @touchmove="handleTouchMove" />

        <!-- Highlight with box-shadow overlay -->
        <div ref="highlightRef" class="spotlight-highlight" />

        <!-- Tooltip -->
        <div ref="tooltipRef" class="spotlight-tooltip" @wheel="handleWheel" @touchstart="handleTouchStart" @touchmove="handleTouchMove">
          <div class="spotlight-tooltip-content">
            <div v-if="currentStepData?.title" class="spotlight-title">
              {{ currentStepData.title }}
            </div>
            <div v-if="currentStepData?.message" class="spotlight-message">
              {{ currentStepData.message }}
            </div>

            <div class="spotlight-footer">
              <span v-if="totalSteps > 1" class="spotlight-progress"> {{ state.currentStep + 1 }} / {{ totalSteps }} </span>
              <div class="spotlight-actions">
                <UButton v-if="!isFirst" variant="ghost" size="sm" @click="prev">
                  {{ t('button.back') }}
                </UButton>
                <UButton v-if="!isLast" variant="solid" size="sm" @click="next">
                  {{ t('button.next') }}
                </UButton>
                <UButton v-else variant="solid" size="sm" @click="close">
                  {{ t('button.understand') }}
                </UButton>
              </div>
            </div>
          </div>

          <!-- Arrow pointer -->
          <div class="spotlight-arrow" :class="`spotlight-arrow-${currentStepData?.placement || 'bottom'}`" />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.spotlight-container {
  position: fixed;
  inset: 0;
  z-index: 9999;
  pointer-events: none;
}

.spotlight-click-blocker {
  position: fixed;
  inset: 0;
  pointer-events: auto;
}

.spotlight-highlight {
  position: fixed;
  border: 2px solid var(--color-primary-500);
  border-radius: 8px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.7);
  pointer-events: none;
  animation: pulse-border 2s ease-in-out infinite;
}

@keyframes pulse-border {
  0%,
  100% {
    border-color: var(--color-primary-500);
  }
  50% {
    border-color: var(--color-primary-400);
  }
}

.spotlight-tooltip {
  position: fixed;
  max-width: 360px;
  min-width: 280px;
  z-index: 10000;
  pointer-events: auto;
}

.spotlight-tooltip-content {
  background: var(--ui-bg);
  border: 1px solid var(--ui-border);
  border-radius: 12px;
  padding: 16px;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.spotlight-title {
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 8px;
  color: var(--ui-text);
}

.spotlight-message {
  font-size: 0.875rem;
  color: var(--ui-text-muted);
  line-height: 1.5;
  margin-bottom: 16px;
}

.spotlight-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.spotlight-progress {
  font-size: 0.75rem;
  color: var(--ui-text-dimmed);
}

.spotlight-actions {
  display: flex;
  gap: 8px;
}

.spotlight-arrow {
  position: absolute;
  width: 12px;
  height: 12px;
  background: var(--ui-bg);
  border: 1px solid var(--ui-border);
  transform: rotate(45deg);
}

.spotlight-arrow-bottom {
  top: -7px;
  left: 50%;
  margin-left: -6px;
  border-right: none;
  border-bottom: none;
}

.spotlight-arrow-top {
  bottom: -7px;
  left: 50%;
  margin-left: -6px;
  border-left: none;
  border-top: none;
}

.spotlight-arrow-left {
  right: -7px;
  top: 50%;
  margin-top: -6px;
  border-left: none;
  border-bottom: none;
}

.spotlight-arrow-right {
  left: -7px;
  top: 50%;
  margin-top: -6px;
  border-right: none;
  border-top: none;
}

/* Transitions */
.spotlight-enter-active,
.spotlight-leave-active {
  transition: opacity 0.3s ease;
}

.spotlight-enter-from,
.spotlight-leave-to {
  opacity: 0;
}
</style>
